"use client";
import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const StandardsSection = () => {
  const navigate = useNavigate();
  const handleOrderClick = () => {
    navigate('/menu');
  }
  const standards = [
    {
      title: "Premium Ingredients Only",
      description: "We use the finest and freshest ingredients to ensure every bite is full of flavor and quality."
    },
    {
      title: "Crafted with Care",
      description: "Each cake and pastry is made with precision, passion, and a personal touch by our expert hands."
    },
    {
      title: "Hygiene & Safety First",
      description: "Our kitchen maintains top hygiene standards to provide safe and trustworthy products."
    },
    {
      title: "Elegant Presentation",
      description: "From classic to custom, every dessert is beautifully designed and packaged to impress."
    },
    {
      title: "Customer-Centric Approach",
      description: "We listen, customize, and deliver exactly what you envision — making every order special."
    },
    {
      title: "Consistency You Can Trust",
      description: "With over 15 years of baking experience, we ensure consistent taste and quality every time."
    }
  ];

  return (
    <motion.section
      className="flex overflow-hidden flex-col items-center px-4 md:px-16 pt-16 pb-20 w-full bg-orange-50"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      aria-labelledby="standards-title"
    >
      <motion.h2
        id="standards-title"
        className="text-4xl md:text-6xl font-semibold text-red-500 text-center mb-4"
        style={{ fontFamily: "'Sansita Swashed', cursive" }}
        initial={{ scale: 0.8, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        viewport={{ once: true }}
      >
        Our Standards
      </motion.h2>

      <motion.div
        className="flex gap-4 md:gap-6 items-center mt-5 justify-center mb-12"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        viewport={{ once: true }}
      >
        <img
          src="https://api.builder.io/api/v1/image/assets/TEMP/a004749eeafe96650e9f1ab184dc488f2a5d7166?placeholderIfAbsent=true&apiKey=d09e0bc02eef4ced83b05308e83d6101"
          alt="Decorative line left"
          className="object-contain shrink-0 self-stretch my-auto aspect-[166.67] w-[120px] md:w-[171px]"
        />
        <img
          src="https://api.builder.io/api/v1/image/assets/TEMP/e146accfc39e5657464e52c2fa7da78cc6a7866e?placeholderIfAbsent=true&apiKey=d09e0bc02eef4ced83b05308e83d6101"
          alt="Decorative cupcake element"
          className="object-contain self-stretch my-auto aspect-square w-[25px] md:w-[35px]"
        />
        <img
          src="https://api.builder.io/api/v1/image/assets/TEMP/42135f6d9b1a4837d8746dac7416759042e4672d?placeholderIfAbsent=true&apiKey=d09e0bc02eef4ced83b05308e83d6101"
          alt="Decorative line right"
          className="object-contain shrink-0 self-stretch my-auto aspect-[166.67] w-[120px] md:w-[172px]"
        />
      </motion.div>

      <div className="self-stretch mt-3 mb-0 w-full max-w-6xl mx-auto">
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-8 justify-center"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
        >
          {standards.map((standard, index) => (
            <motion.article
              key={standard.title}
              className="flex items-start gap-4 p-6 bg-white rounded-2xl border-2 border-orange-200 hover:border-red-500 transition-all duration-300 hover:shadow-lg"
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, y: -5 }}
            >
              {/* Checkmark */}
              <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mt-1">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M16.7071 5.29289C17.0976 5.68342 17.0976 6.31658 16.7071 6.70711L8.70711 14.7071C8.31658 15.0976 7.68342 15.0976 7.29289 14.7071L3.29289 10.7071C2.90237 10.3166 2.90237 9.68342 3.29289 9.29289C3.68342 8.90237 4.31658 8.90237 4.70711 9.29289L8 12.5858L15.2929 5.29289C15.6834 4.90237 16.3166 4.90237 16.7071 5.29289Z"
                    fill="white"
                  />
                </svg>
              </div>

              {/* Content */}
              <div className="flex-1">
                <h3
                  className="text-xl md:text-2xl font-semibold text-red-500 mb-3"
                  style={{ fontFamily: "'Sansita Swashed', cursive" }}
                >
                  {standard.title}
                </h3>
                <p className="text-base leading-6 text-cyan-900">
                  {standard.description}
                </p>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>

      {/* Call to Action */}
      <motion.div
        className="mt-16 text-center"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.4 }}
        viewport={{ once: true }}
      >
        <motion.button
          className="flex gap-2 justify-center items-center px-8 md:px-11 py-4 md:py-5 bg-red-500 rounded-[40.5px] text-lg md:text-xl font-bold text-white hover:bg-red-600 transition-colors mx-auto"
          onClick={handleOrderClick}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Order Your Perfect Cake
        </motion.button>
      </motion.div>
    </motion.section>
  );
};

export default StandardsSection;
